import logError from 'src/data/services/logError';
import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, Platform, StyleSheet } from 'react-native';
import showAlert from 'src/core/utils/showAlert';
import errorHandler from 'src/core/utils/errorHandler';
import type { TextStyle } from 'react-native';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import FormField from 'src/presentation/components/atoms/FormField';
import SubmitButton from 'src/presentation/components/atoms/SubmitButton';
import Translations from 'src/core/utils/Translations';
import Config from 'src/core/utils/Config';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';

const isIos = Platform.OS === 'ios';

function FeedbackForm() {
	const contentWidth = useContentWidth();
	const navigationStore = useNavigationStore();
	const formStore = useFormStore();
	const userProfile = useAuthenticationStore((state) => state.userProfile);
	// A logged-in user's name is known: prefill it and hide the field (it is still submitted).
	const knownName = userProfile ? [userProfile.firstname, userProfile.lastname].filter(Boolean).join(' ').trim() : '';
	const [name, setName] = useState<string>(knownName);
	useEffect(() => {
		if (knownName) setName(knownName);
	}, [knownName]);
	const [feedback, setFeedback] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errors, setErrors] = useState<{ feedback?: string }>({});

	const sizedStyles = useMemo(
		(): { helperText: TextStyle } => ({
			helperText: {
				fontSize: contentWidth < 321 ? Dimens.fontSize10 : Dimens.fontSize12,
			},
		}),
		[contentWidth],
	);

	const onSubmit = () => {
		if (!feedback.trim()) {
			setErrors({ feedback: Translations.errorFieldRequired });
			return;
		}

		setErrors({});
		setIsLoading(true);

		const supportMail = Config.supportMail;

		formStore
			// The account's e-mail travels along so beheer can see who wrote this and reply; feedback from a
			// logged-out visitor carries no address, and then the name is all there is (#404).
			.sendFeedback({
				contact_person: supportMail,
				title: 'Topicus Events feedback',
				naam: name,
				email: userProfile?.email ?? '',
				feedback,
			})
			.then(() => {
				showAlert({
					title: Translations.successFeedbackTitle,
					message: Translations.successFeedbackMessage,
					onConfirm: () => navigationStore.goBack(),
				});
			})
			.catch((error) => {
				logError(error);
				// Explicit submit action: show a clear failure (the call opts out of the global toast).
				errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
			})
			.finally(() => setIsLoading(false));
	};

	return (
		<View>
			{!knownName && (
				<FormField
					label={Translations.labelYourName}
					placeholder={Translations.placeholderYourName}
					value={name}
					onChangeText={setName}
				/>
			)}

			{isIos ? <Text style={[styles.helperText, sizedStyles.helperText]}>{Translations.textFeedbackHelper}</Text> : null}

			<FormField
				error={errors.feedback}
				label={Translations.labelTheFeedback}
				multiline={true}
				numberOfLines={4}
				placeholder={isIos ? Translations.placeholderFeedback : Translations.placeholderFeedbackShort}
				required={true}
				style={isIos ? styles.feedbackInput : undefined}
				value={feedback}
				onChangeText={(text) => {
					setFeedback(text);
					if (errors.feedback) setErrors({});
				}}
			/>

			<SubmitButton
				isLoading={isLoading}
				label={Translations.buttonSubmit}
				style={isIos ? styles.submitIos : styles.submitAndroid}
				onPress={onSubmit}
			/>
		</View>
	);
}

export default FeedbackForm;

const styles = StyleSheet.create({
	helperText: {
		fontFamily: Fonts.montserratRegular,
		color: Colors.greyTextDark,
		paddingHorizontal: Dimens.padding20,
		paddingTop: Dimens.padding5,
	},
	feedbackInput: {
		fontSize: Dimens.fontSize14,
		lineHeight: 19,
		paddingLeft: Dimens.padding5,
		borderWidth: 1,
		borderColor: Colors.lightGrey,
		borderRadius: 5,
		textAlignVertical: 'top',
		height: 100,
		paddingTop: Dimens.padding10,
	},
	submitIos: {
		flexDirection: 'row',
		justifyContent: 'center',
		marginTop: Dimens.margin40,
	},
	submitAndroid: {
		flexDirection: 'row',
		justifyContent: 'center',
		marginTop: Dimens.margin48,
	},
});
